import React from 'react';
import { Loader2, Briefcase } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { ModeNotificationsContext, FournisseurEnregistrement, EtatEnregistrement } from '../ui/LigneNotification';
import { SuiviPrivatisationNotificationBlocks, DevisNotifications } from '../Settings/SuiviPrivatisationNotificationBlocks';

const EMPTY: DevisNotifications = {
    plaquette_send_subject: '', plaquette_send_body: '', plaquette_default_template_id: '',
    devis_send_subject: '', devis_send_body: '',
    notify_devis_signed_received: true, devis_signed_received_subject: '', devis_signed_received_body: '',
    notify_devis_declined: true, devis_declined_subject: '', devis_declined_body: '',
};

/**
 * Réglages des emails du module Suivi privatisation, ouverts directement depuis le module (bouton
 * « Emails ») — même principe que les autres modules migrés. Réutilise le bloc UI existant
 * (SuiviPrivatisationNotificationBlocks) tel quel, juste sorti de l'onglet central Paramètres >
 * Notifications. Deux sections PATCH distinctes : "devis_privatisation" (plaquette/devis envoyés,
 * propres à ce module) et "devis_prestation" (devis signé/décliné — colonnes partagées avec le module
 * Devis Prestation, les deux verticaux n'étant jamais actifs en même temps sur un même compte).
 */
export function SuiviPrivatisationEmailSettingsModal({ onClose }: { onClose: () => void }) {
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [saved, setSaved] = React.useState(false);
    const [erreur, setErreur] = React.useState('');
    const [valeurs, setValeurs] = React.useState<DevisNotifications>(EMPTY);

    React.useEffect(() => {
        moduleApi.getClientNotificationSettings()
            .then((s: any) => {
                setValeurs({
                    plaquette_send_subject: s?.plaquette_send_subject || '',
                    plaquette_send_body: s?.plaquette_send_body || '',
                    plaquette_default_template_id: s?.plaquette_default_template_id || '',
                    devis_send_subject: s?.devis_send_subject || '',
                    devis_send_body: s?.devis_send_body || '',
                    notify_devis_signed_received: s ? !!s.notify_devis_signed_received : true,
                    devis_signed_received_subject: s?.devis_signed_received_subject || '',
                    devis_signed_received_body: s?.devis_signed_received_body || '',
                    notify_devis_declined: s ? !!s.notify_devis_declined : true,
                    devis_declined_subject: s?.devis_declined_subject || '',
                    devis_declined_body: s?.devis_declined_body || '',
                });
            })
            .catch((e: any) => setErreur(e?.message || 'Erreur de chargement'))
            .finally(() => setLoading(false));
    }, []);

    const enregistrer = async () => {
        setSaving(true); setErreur(''); setSaved(false);
        try {
            const [r1, r2]: [any, any] = await Promise.all([
                moduleApi.patchClientNotificationSettings('devis_privatisation', {
                    plaquette_send_subject: valeurs.plaquette_send_subject, plaquette_send_body: valeurs.plaquette_send_body,
                    plaquette_default_template_id: valeurs.plaquette_default_template_id,
                    devis_send_subject: valeurs.devis_send_subject, devis_send_body: valeurs.devis_send_body,
                }),
                moduleApi.patchClientNotificationSettings('devis_prestation', {
                    notify_devis_signed_received: valeurs.notify_devis_signed_received,
                    devis_signed_received_subject: valeurs.devis_signed_received_subject, devis_signed_received_body: valeurs.devis_signed_received_body,
                    notify_devis_declined: valeurs.notify_devis_declined,
                    devis_declined_subject: valeurs.devis_declined_subject, devis_declined_body: valeurs.devis_declined_body,
                }),
            ]);
            if (r1?.error || r2?.error) { setErreur(r1?.error || r2?.error); return; }
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch (e: any) {
            setErreur(e?.message || "Erreur lors de l'enregistrement.");
        } finally { setSaving(false); }
    };

    return (
        <Modal open onClose={onClose} size="lg" icon={<Briefcase size={16} />} title="Paramètres des emails de Suivi privatisation"
            subtitle="Un clic sur un titre ouvre son texte et l'aperçu ; l'interrupteur active ou coupe l'email."
            footer={<Button variant="outline" onClick={onClose}>Fermer</Button>}>
            {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="animate-spin text-content-muted" size={28} /></div>
            ) : (
                <ModeNotificationsContext.Provider value={{ enLignes: true }}>
                    <FournisseurEnregistrement enregistrer={enregistrer}>
                        <div className="space-y-3">
                            <SuiviPrivatisationNotificationBlocks
                                valeurs={valeurs}
                                onChange={(patch) => setValeurs((prev) => ({ ...prev, ...patch }))} />
                            {erreur && <p className="text-[12px] font-semibold text-state-danger">{erreur}</p>}
                        </div>
                        <EtatEnregistrement etat={saving ? 'en_cours' : erreur ? 'echec' : saved ? 'fait' : 'repos'} />
                    </FournisseurEnregistrement>
                </ModeNotificationsContext.Provider>
            )}
        </Modal>
    );
}
